import type { ImageLoaderProps } from 'next/image'
import { type ContentfulImagesAPI } from '@lib/types'
import {
  aspectRatioStringToNumber,
  CONTENTFUL_IMAGE_DEFAULT_QUALITY,
} from '@lib/services/image-service'
import { omitBy, isUndefined } from 'lodash'

/**
 * Next.js image loader for Contentful Images API.
 *
 * Generates optimized Contentful image URLs for use with Next.js <Image /> components.
 * Supports custom Contentful image parameters (quality, format, aspect ratio, etc.)
 * and automatically calculates height from aspect ratio if provided.
 *
 * @param loaderProps - Props provided by Next.js <Image /> (src, width, quality, etc.)
 * @param contentfulParams - Optional Contentful image API parameters (ar, fm, q, fit, etc.)
 * @returns The full Contentful image URL with applied transformations.
 *
 * @see https://www.contentful.com/developers/docs/references/images-api/
 */
export const contentfulImageLoader = (
  loaderProps: ImageLoaderProps,
  contentfulParams?: ContentfulImagesAPI
) => {
  let params: ContentfulImagesAPI = { ...contentfulParams }

  // Calculate aspect ratio if provided
  if (params.ar) {
    const aspectRatio = aspectRatioStringToNumber(params.ar)
    params.h =
      aspectRatio !== undefined ? String(Math.round(loaderProps.width / aspectRatio)) : undefined
  }

  // Always set width and quality
  params.w = String(loaderProps.width)
  params.q = String(loaderProps.quality ?? params.q ?? CONTENTFUL_IMAGE_DEFAULT_QUALITY)

  // Remove params that should not be sent
  delete params.ar
  params = omitBy(params, isUndefined)

  return `${loaderProps.src}?${new URLSearchParams(params as { [key: string]: string }).toString()}`
}
